import { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarIcon } from './icons';
import { useTranslation } from 'react-i18next';

export interface ReminderCrop {
  /** Unique key, e.g. "{cropTypeId}_{regionId}" */
  id: string;
  cropTypeName: string;
  regionName: string;
  /** ISO date string "YYYY-MM-DD" — undefined if season not configured */
  seasonStartDate?: string;
}

export interface ReminderCustomer {
  fullName: string;
  phone: string;
  address?: string;
}

export interface ReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Customer's crops for the selector and date pre-fill */
  crops: ReminderCrop[];
  /** Pre-selected crop id */
  initialCropId?: string;
  /** Pre-filled description (e.g. from a log entry) */
  initialDescription?: string;
  /** Customer details appended as a separate section in the description */
  customerInfo?: ReminderCustomer;
}

function buildGCalUrl(title: string, date: string, time: string, description: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);

  function pad(n: number) { return String(n).padStart(2, '0'); }

  const start = `${year}${pad(month)}${pad(day)}T${pad(hour)}${pad(minute)}00`;
  // End = start + 1 hour
  const endHour = hour + 1;
  const end = `${year}${pad(month)}${pad(day)}T${pad(endHour < 24 ? endHour : 23)}${pad(endHour < 24 ? minute : 59)}00`;

  // Use the /render?action=TEMPLATE format — the standard "Add to Google
  // Calendar" deep-link that works correctly on Android (native app) and iOS.
  // The '/r/eventedit' path is a web-app route that Android doesn't handle.
  // Keep the '/' in dates literal — URLSearchParams would encode it as %2F.
  const query =
    `action=TEMPLATE` +
    `&text=${encodeURIComponent(title)}` +
    `&dates=${start}/${end}` +
    `&details=${encodeURIComponent(description)}`;
  return `https://calendar.google.com/calendar/render?${query}`;
}

function buildICS(title: string, date: string, time: string, description: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);

  function pad(n: number) { return String(n).padStart(2, '0'); }

  const dtStart = `${year}${pad(month)}${pad(day)}T${pad(hour)}${pad(minute)}00`;
  const endHour = hour + 1;
  const dtEnd = `${year}${pad(month)}${pad(day)}T${pad(endHour < 24 ? endHour : 23)}${pad(endHour < 24 ? minute : 59)}00`;
  const uid = `${Date.now()}@agrobook`;
  const safeDesc = description.replace(/\n/g, '\\n');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AgroBook//Reminder//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${safeDesc}`,
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

function downloadICS(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function buildCustomerSection(customer: ReminderCustomer): string {
  const lines = ['---', customer.fullName, customer.phone];
  if (customer.address) lines.push(customer.address);
  return lines.join('\n');
}

function buildTitle(crop: ReminderCrop, t: (_key: string, _opts?: Record<string, string>) => string): string {
  return t('reminder.generatedTitle', { crop: crop.cropTypeName, region: crop.regionName });
}

export function ReminderModal({
  isOpen,
  onClose,
  crops,
  initialCropId,
  initialDescription = '',
  customerInfo,
}: ReminderModalProps) {
  const { t } = useTranslation();
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const autoResizeDescription = useCallback(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const getInitialCrop = useCallback(() =>
    crops.find(c => c.id === initialCropId) ?? crops[0] ?? null,
  [crops, initialCropId]);

  const [selectedCropId, setSelectedCropId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [description, setDescription] = useState(initialDescription);

  // Track whether user has manually overridden auto-filled fields
  const titleManual = useRef(false);
  const dateManual = useRef(false);

  // Reset state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    titleManual.current = false;
    dateManual.current = false;
    const customerSection = customerInfo ? buildCustomerSection(customerInfo) : '';
    const fullDescription = [initialDescription, customerSection].filter(Boolean).join('\n\n');
    setDescription(fullDescription);
    setTime('09:00');

    const crop = getInitialCrop();
    if (crop) {
      setSelectedCropId(crop.id);
      setTitle(buildTitle(crop, t));
      setDate(crop.seasonStartDate ?? '');
    } else {
      setSelectedCropId('');
      setTitle('');
      setDate('');
    }
    // Resize after state updates are flushed
    setTimeout(autoResizeDescription, 0);
  }, [isOpen, initialDescription, customerInfo, getInitialCrop, autoResizeDescription, t]);

  // Focus trap + Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  function handleCropChange(cropId: string) {
    setSelectedCropId(cropId);
    const crop = crops.find(c => c.id === cropId) ?? null;
    if (crop) {
      if (!titleManual.current) setTitle(buildTitle(crop, t));
      if (!dateManual.current) setDate(crop.seasonStartDate ?? '');
    } else {
      if (!titleManual.current) setTitle('');
      if (!dateManual.current) setDate('');
    }
  }

  function handleAddToGCal() {
    if (!date || !title) return;
    const url = buildGCalUrl(title, date, time, description);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handleDownloadICS() {
    if (!date || !title) return;
    const content = buildICS(title, date, time, description);
    downloadICS('agrobook-reminder.ics', content);
  }

  const canExport = !!title.trim() && !!date;

  if (!isOpen) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 sm:p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal / bottom-sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('reminder.title')}
        className={[
          'flex w-full flex-col bg-white',
          /* Mobile: bottom-sheet with slide-up animation */
          'fixed bottom-0 rounded-t-2xl animate-slide-up-sheet',
          /* Desktop: centered card, no animation */
          'sm:static sm:max-w-md sm:rounded-xl sm:shadow-xl sm:animate-none',
        ].join(' ')}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 className="font-display text-base font-semibold text-green-900 flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-flex h-4 w-4 shrink-0 text-green-700"><CalendarIcon /></span>
            {t('reminder.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('reminder.cancel')}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
          >
            <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 2l12 12M14 2L2 14" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
          {/* Crop selector */}
          {crops.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t('reminder.cropLabel')}
              </label>
              <select
                value={selectedCropId}
                onChange={e => handleCropChange(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm text-gray-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              >
                <option value="">{t('reminder.cropNone')}</option>
                {crops.map(crop => (
                  <option key={crop.id} value={crop.id}>
                    {crop.cropTypeName} — {crop.regionName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div className="flex flex-col gap-1">
            <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t('reminder.titleLabel')}
            </label>
            <input
              type="text"
              value={title}
              onChange={e => { setTitle(e.target.value); titleManual.current = true; }}
              className="rounded-md border border-gray-300 px-3 py-2 font-body text-sm text-gray-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </div>

          {/* Date + Time row */}
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t('reminder.dateLabel')}
              </label>
              <input
                type="date"
                value={date}
                onChange={e => { setDate(e.target.value); dateManual.current = true; }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 font-body text-sm text-gray-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
            <div className="flex w-28 flex-col gap-1">
              <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t('reminder.timeLabel')}
              </label>
              <input
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 font-body text-sm text-gray-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1">
            <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t('reminder.descriptionLabel')}
            </label>
            <textarea
              ref={descriptionRef}
              rows={3}
              value={description}
              onChange={e => { setDescription(e.target.value); autoResizeDescription(); }}
              className="max-h-48 w-full resize-none overflow-y-auto rounded-md border border-gray-300 px-3 py-2 font-body text-sm text-gray-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="order-last rounded-md px-4 py-2 font-body text-sm text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 sm:order-first"
          >
            {t('reminder.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDownloadICS}
            disabled={!canExport}
            className="rounded-md border border-green-700 px-4 py-2 font-body text-sm font-semibold text-green-700 hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('reminder.downloadICS')}
          </button>
          <button
            type="button"
            onClick={handleAddToGCal}
            disabled={!canExport}
            className="rounded-md bg-green-700 px-4 py-2 font-body text-sm font-semibold text-white hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('reminder.addToGCal')}
          </button>
        </div>
      </div>
    </div>
  );
}
